"use client";

import React, { useMemo } from "react";
import { useTranslations } from "next-intl";
import {
  MdErrorOutline,
  MdAutoAwesome,
  MdVisibilityOff,
  MdAttachMoney,
} from "react-icons/md";
import { CircleFlag } from "react-circle-flags";
import { useListingContext } from "../listingContex";
import styles from "./pricing-content.module.css";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { getCurrency } from "@/lib/currency-data/currency";
import { useCurrenciesContext } from "@/global-context/currencies-context";
import { Currencies, UserTypes } from "@/types/types";

export default function PricingAndContentSection() {
  const t = useTranslations("NewListing"); // Ensure access to errors namespace
  const { data, updateField, validationErrors } = useListingContext();

  const { userData } = useUserAuthDataContext();
  const userCurrency = getCurrency(userData.currency);
  const { currenciesRates } = useCurrenciesContext();
  const currencyKey = `${userCurrency.Name}-EUR` as keyof Currencies;
  const rate: number = currenciesRates[currencyKey];

  if (!data) return <PricingAndContentSkeleton />;

  // Formatting utilities
  const numberFormatter = new Intl.NumberFormat("en-US");

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, ""); // Remove non-digits
    const numericValue = Number(newValue) || 0;

    // Store exact float in context. e.g. 10 USD * 0.93 = 9.3 EUR
    updateField("price", numericValue * rate);
  };

  // Dynamic Description Limits
  const descriptionLimits: Record<string, number> = {
    essential: 2000,
    plus: 2500,
    prime: 3000,
    spotlight: 3500,
  };
  const maxDescLength = descriptionLimits[data.listing_type] || 2000;
  const displayPrice = data.price ? Math.round(data.price / rate) : 0;

  return (
    <section className={styles.transparentContainer}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Pricing & Content</h2>
        <p className={styles.sectionSubtitle}>
          Set your price and write a compelling description to attract buyers.
        </p>
      </div>

      {/* 1. TITLE INPUT */}
      <div id="title" className={styles.inputGroup}>
        <div className={styles.labelRow}>
          <div className={styles.labelWithInfo}>
            <label className={styles.inputLabel}>
              Listing Title <span className={styles.asterisk}>*</span>
            </label>
            {validationErrors?.title && (
              <span className={styles.inlineError}>
                <MdErrorOutline /> {t(`errors.${validationErrors.title}`)}
              </span>
            )}
          </div>
          <span
            className={`${styles.charCount} ${(data.title?.length || 0) > 100 ? styles.textError : ""}`}
          >
            {data.title?.length || 0} / 100
          </span>
        </div>
        <input
          type="text"
          maxLength={100}
          value={data.title || ""}
          onChange={(e) => updateField("title", e.target.value)}
          className={`${styles.textInput} ${validationErrors?.title ? styles.inputError : ""}`}
          placeholder="e.g. Stunning 3-Bedroom Penthouse with Marina Views"
        />
      </div>

      {/* 2. PRICE INPUT (With User Currency & EUR Approx) */}
      <div id="price" className={styles.inputGroup}>
        <div className={styles.labelWithInfo}>
          <label className={styles.inputLabel}>
            Price <span className={styles.asterisk}>*</span>
          </label>
          {validationErrors?.price && (
            <span className={styles.inlineError}>
              <MdErrorOutline /> {t(`errors.${validationErrors.price}`)}
            </span>
          )}
        </div>

        <div className={styles.priceContainer}>
          {/* Main Input Box */}
          <div
            className={`${styles.mainPriceInputWrapper} ${validationErrors?.price ? styles.inputError : ""}`}
          >
            <div className={styles.currencyPill}>
              <CircleFlag
                className={styles.flagIcon}
                countryCode={userCurrency.Code.toLowerCase()}
              />
              <span className={styles.currencyName}>{userCurrency.Name}</span>
            </div>
            <input
              type="text"
              min={0}
              // Display the converted local price
              value={displayPrice ? numberFormatter.format(displayPrice) : ""}
              onChange={handlePriceChange}
              className={styles.priceInput}
              placeholder="0"
            />
          </div>

          {/* Approx EUR Box (Only show if not already EUR) */}
          {userCurrency.Name !== "EUR" && (
            <div className={styles.approxEurWrapper}>
              <span className={styles.approxSign}>≈</span>
              <span className={styles.approxValue}>
                {/* data.price is already in EUR! Just round it for display */}
                {numberFormatter.format(Math.round(data.price))}
              </span>
              <span className={styles.approxCurrency}>EUR</span>
              <CircleFlag className={styles.flagIconSmall} countryCode={"eu"} />
            </div>
          )}
        </div>
        <div className={styles.priceControlsRow}>
          <button
            type="button"
            className={`${styles.togglePill} ${data.price_on_request ? styles.togglePillActive : ""}`}
            onClick={(e) => {
              e.preventDefault();
              updateField("price_on_request", !data.price_on_request);
            }}
          >
            {/* Import MdVisibilityOff from react-icons/md if you haven't */}
            {data.price_on_request ? (
              <>
                <MdVisibilityOff size={16} className={styles.controlIcon} />
                <span>Price Hidden</span>
              </>
            ) : (
              <>
                <MdAttachMoney size={16} className={styles.controlIcon} />
                <span>Showing Price</span>
              </>
            )}
          </button>

          {data.purpose === "for-rent" && (
            <div className={styles.segmentedControl}>
              <button
                type="button"
                className={`${styles.segmentOption} ${!data.is_price_per_year ? styles.segmentActive : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  updateField("is_price_per_year", false);
                }}
              >
                / Month
              </button>
              <button
                type="button"
                className={`${styles.segmentOption} ${data.is_price_per_year ? styles.segmentActive : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  updateField("is_price_per_year", true);
                }}
              >
                / Year
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 3. DESCRIPTION TEXTAREA */}
      <div id="description" className={styles.inputGroup}>
        <div className={styles.labelRow}>
          <div className={styles.labelWithInfo}>
            <label className={styles.inputLabel}>
              Description <span className={styles.asterisk}>*</span>
            </label>
            {validationErrors?.description && (
              <span className={styles.inlineError}>
                <MdErrorOutline /> {t(`errors.${validationErrors.description}`)}
              </span>
            )}
          </div>
          <span
            className={`${styles.charCount} ${(data.description?.length || 0) > maxDescLength ? styles.textError : ""}`}
            style={{ alignSelf: "flex-start", paddingTop: "4px" }}
          >
            {data.description?.length || 0} / {maxDescLength}
          </span>
        </div>
        <textarea
          value={data.description || ""}
          onChange={(e) => updateField("description", e.target.value)}
          className={`${styles.textArea} ${validationErrors?.description ? styles.inputError : ""}`}
          maxLength={maxDescLength}
          placeholder="Describe the property's best features, recent renovations, and surrounding neighborhood..."
        />
        <div className={styles.helperText}>
          Your current{" "}
          <strong className={styles.planHighlight}>{data.listing_type}</strong>{" "}
          plan allows up to {numberFormatter.format(maxDescLength)} characters.
        </div>
      </div>

      {/* 4. AI NOTES TEXTAREA */}
      {userData.user_type != UserTypes.PRIVATEUSER && (
        <div id="ai_notes" className={styles.inputGroup}>
          <div className={styles.labelRow}>
            <div className={styles.labelWithInfo}>
              <label className={styles.aiLabel}>
                <MdAutoAwesome className={styles.aiIcon} />
                AI Listing Notes
              </label>
              {validationErrors?.ai_notes && (
                <span className={styles.inlineError}>
                  <MdErrorOutline /> {t(`errors.${validationErrors.ai_notes}`)}
                </span>
              )}
            </div>
            <span
              className={`${styles.charCount} ${(data.ai_notes?.length || 0) > 1000 ? styles.textError : ""}`}
              style={{ alignSelf: "flex-start", paddingTop: "4px" }}
            >
              {data.ai_notes?.length || 0} / 1000
            </span>
          </div>
          <textarea
            value={data.ai_notes || ""}
            onChange={(e) => updateField("ai_notes", e.target.value)}
            className={`${styles.textArea} ${styles.aiTextArea} ${validationErrors?.ai_notes ? styles.inputError : ""}`}
            maxLength={1000}
            placeholder="Add rough bullet points here. Our AI will use these notes to enhance your final description if requested..."
          />
        </div>
      )}
    </section>
  );
}

export function PricingAndContentSkeleton() {
  return (
    <section className={styles.transparentContainer}>
      {/* Title & Subtitle */}
      <div className={styles.sectionHeader}>
        <div className={`${styles.skeletonPulse} ${styles.skelTitle}`} />
        <div className={`${styles.skeletonPulse} ${styles.skelSubtitle}`} />
      </div>

      {/* 1. TITLE INPUT */}
      <div className={styles.inputGroup}>
        <div className={styles.labelRow}>
          <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
          <div className={`${styles.skeletonPulse} ${styles.skelCharCount}`} />
        </div>
        <div className={`${styles.skeletonPulse} ${styles.skelInput}`} />
      </div>

      {/* 2. PRICE INPUT */}
      <div className={styles.inputGroup}>
        <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
        <div className={styles.priceContainer}>
          <div className={`${styles.skeletonPulse} ${styles.skelPriceInput}`} />
          <div className={`${styles.skeletonPulse} ${styles.skelApproxBox}`} />
        </div>
        <div className={styles.priceControlsRow}>
          <div className={`${styles.skeletonPulse} ${styles.skelToggle}`} />
          <div className={`${styles.skeletonPulse} ${styles.skelToggle}`} />
        </div>
      </div>

      {/* 3. DESCRIPTION TEXTAREA */}
      <div className={styles.inputGroup}>
        <div className={styles.labelRow}>
          <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
          <div className={`${styles.skeletonPulse} ${styles.skelCharCount}`} />
        </div>
        <div className={`${styles.skeletonPulse} ${styles.skelTextArea}`} />
        <div className={`${styles.skeletonPulse} ${styles.skelHelperText}`} />
      </div>

      {/* 4. AI NOTES TEXTAREA */}
      <div className={styles.inputGroup}>
        <div className={styles.labelRow}>
          <div className={`${styles.skeletonPulse} ${styles.skelLabel}`} />
          <div className={`${styles.skeletonPulse} ${styles.skelCharCount}`} />
        </div>
        <div className={`${styles.skeletonPulse} ${styles.skelAiTextArea}`} />
      </div>
    </section>
  );
}
